import { Vue, Component, Prop } from "vue-property-decorator";
import {
    BButton,
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroupAppend,
} from "bootstrap-vue";
import VueSelect from "vue-select";
import {
    ClientMessageDirection,
    IClient,
    IClientMessage,
    ICompany,
    IEmployee,
    IOrder,
    IOrderType,
    IPluginBinding,
    IPluginCommunicationSendMessageDto,
    IProductType,
    ITemplateNotification,
    IUser,
} from "@lib";
import AltIcon from "@/core/components/alt-icon";
import { Formatter } from "@/utils/formatter";
import { AppException } from "@/core/exceptions";
import { OrderPrintContext } from "@/@core/usecases/template/macro-replacers/order-document.macro-replacer";
import { Printer } from "@/@core/usecases/template/printer";

@Component({
    components: {
        AltIcon,
        BButton,
        BDropdown,
        BDropdownDivider,
        BDropdownItem,
        BFormGroup,
        BFormTextarea,
        BFormInput,
        BInputGroupAppend,
        VueSelect,
    },
})
export default class OrderViewTabsCommunication extends Vue {
    @Prop({ required: true })
    public order!: IOrder;

    @Prop({ required: true })
    private orderType!: IOrderType;

    @Prop({ default: () => [] })
    private productTypes!: IProductType[];

    @Prop()
    private manager?: IEmployee;

    @Prop({ required: true })
    public client!: IClient | null;

    @Prop({ type: Array, default: () => [] })
    public plugins!: IPluginBinding[];

    @Prop({ type: Array, default: () => [] })
    public notificationTemplates!: ITemplateNotification[];

    @Prop({ type: Boolean, default: false })
    public readonly!: boolean;

    public get isCommunicationEnabled(): boolean {
        return this.isCommunicationByTelegramEnabled || this.isCommunicationByMaxEnabled;
    }

    public get isCommunicationByTelegramEnabled(): boolean {
        const channelEnabled = !!this.pluginCurrent?.params?.find(p => p.name === "channel" && p.value === "telegram");
        const externalIdSpecified = !!this.client?.info?.contacts?.social?.telegram;
        return channelEnabled && externalIdSpecified;
    }

    public get isCommunicationByMaxEnabled(): boolean {
        const channelEnabled = !!this.pluginCurrent?.params?.find(p => p.name === "channel" && p.value === "max");
        const externalIdSpecified = !!this.client?.info?.contacts?.social?.max;
        return channelEnabled && externalIdSpecified;
    }

    private company!: ICompany;
    private user!: IUser;

    public pluginCurrent: IPluginBinding | null = null;
    public messages: IClientMessage[] = [];
    public messagesTotal: number = 0;
    public messageText: string = "";

    public async mounted(): Promise<void> {
        try {
            this.company = await this.$info.getCompany();
            this.user = await this.$info.getUser();
            this.pluginCurrent = this.plugins.length > 0 ? this.plugins[0] : null;

            await this.selectMessages();
        } catch (e: any) {
            this.$alt.toast.error(e.message);
        }
    }

    private async selectMessages(): Promise<void> {
        try {
            if (!this.client) {
                throw new AppException("Клиент не задан.");
            }

            const result = await this.$alt.system.usecase
                .createClientMessageUseCase()
                .select(this.company.id, this.client.id);

            // TODO
            /*this.messages = [
                {
                    id: "4",
                    companyId: "1",
                    clientId: "1",
                    direction: ClientMessageDirection.Income,
                    channel: "telegram",
                    text: "iPhone X готов. Альт ЦРМ. К оплате:  0,00",
                    createdAt: new Date(),
                    updatedAt: new Date(),
                },
                {
                    id: "3",
                    companyId: "1",
                    clientId: "1",
                    direction: ClientMessageDirection.Outcome,
                    channel: "telegram",
                    text: "iPhone X готов. Альт ЦРМ. К оплате:  0,00",
                    senderId: this.user.id,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    senderRef: this.user,
                },
                {
                    id: "2",
                    companyId: "1",
                    clientId: "1",
                    direction: ClientMessageDirection.Income,
                    channel: "telegram",
                    text: "iPhone X готов. Альт ЦРМ. К оплате:  0,00",
                    createdAt: new Date(),
                    updatedAt: new Date(),
                },
                {
                    id: "1",
                    companyId: "1",
                    clientId: "1",
                    direction: ClientMessageDirection.Outcome,
                    channel: "telegram",
                    text: "iPhone X готов. Альт ЦРМ. К оплате:  0,00",
                    createdAt: new Date(),
                    updatedAt: new Date(),
                },
            ];*/

            this.messages.unshift(...result.data);
            this.messagesTotal = result.total;
        } catch (e: any) {
            throw new AppException(`Не удалось загрузить сообщения: ${e.message}`);
        }
    }

    public getAuthorName(message: IClientMessage): string {
        if (message.direction === ClientMessageDirection.Income) {
            return "Клиент";
        }

        if (!message.senderId) {
            return "Система";
        }

        const name = message.senderRef?.info?.name ?? "Сотрудник";

        return message.senderId === this.user.id ? `${name} (Вы)` : name;
    }

    public formatDatetime(datetime: Date): string {
        return Formatter.datetime(datetime, { format: "L LT" });
    }

    public fromTemplate(template: ITemplateNotification): void {
        if (!this.order.officeRef) {
            return;
        }

        const context: OrderPrintContext = {
            company: this.company,
            office: this.order.officeRef,
            order: this.order,
            orderType: this.orderType,
            productTypes: this.productTypes,
            manager: this.manager,
            plugins: this.plugins,
        };

        this.messageText = Printer.replaceOrderDocumentsGeneralMacros(template.template, context);
    }

    public async sendMessage(channel: string): Promise<void> {
        try {
            if (this.messageText.length === 0) {
                return;
            }

            if (!this.pluginCurrent) {
                throw new AppException("Плагин не задан.");
            }

            if (!this.client) {
                throw new AppException("Клиент не задан.");
            }

            const dto: IPluginCommunicationSendMessageDto = {
                clientId: this.client.id,
                text: this.messageText,
                channel: channel,
            };

            this.$alt.loader.show();

            const result = await this.$alt.system.usecase
                .createPluginUseCase()
                .clientCommunicationSendMessage(this.pluginCurrent.company, this.pluginCurrent.id, dto);

            // TODO: вычесть с баланса, если надо
            // TODO: обновить ui

            this.messages.unshift(result.message);
            this.messagesTotal += 1;

            this.messageText = "";
            //this.$emit("order-changed", order);

            this.$alt.toast.success("Сообщение успешно отправлено.");
        } catch {
            this.$alt.toast.error("Не удалось отправить сообщение.");
        } finally {
            this.$alt.loader.hide();
        }
    }
}
